import { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import Dashboard from './components/Dashboard';
import Chats from './components/Chats';
import DealsPage from './components/DealsPage';
import Warehouse from './components/Warehouse';
import AiAgent from './components/AiAgent';
import Integrations from './components/Integrations';
import PricingPage from './components/PricingPage';
import TeamPage from './components/TeamPage';
import ProfilePage from './components/ProfilePage';
import SettingsPage from './components/SettingsPage';
import ApprovalsPage from './components/ApprovalsPage';
import LegalPage from './components/LegalPage';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import { AuthModal } from './components/AuthModal';
import SeoHead from './components/SeoHead';
import { fetchApi } from './api';
import { useAuth } from './context/AuthContext';
import { useDialogAccessibility } from './hooks/useDialogAccessibility';
import { NAVIGATE_EVENT, isRouterView, type NavigateDetail } from './shared/notificationTargets';
import { RotateCcw, WifiOff } from 'lucide-react';

function App() {
  useDialogAccessibility();
  const [activeView, setActiveView] = useState<string>(() => {
    const hash = window.location.hash.replace('#', '');
    if (isRouterView(hash)) return hash;
    const stored = localStorage.getItem('clientix_active_view');
    return stored && isRouterView(stored) ? stored : 'dashboard';
  });
  const [selectedChatId, setSelectedChatId] = useState<number | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [autopilot, setAutopilot] = useState(false);
  const [apiIssue, setApiIssue] = useState<string | null>(null);
  const { user, loadingAuth } = useAuth();

  const changeActiveView = (view: string) => {
    setActiveView(view);
    localStorage.setItem('clientix_active_view', view);
    window.location.hash = view;
    setIsSidebarOpen(false);
    document.body.classList.remove('sb-open');
    requestAnimationFrame(() => document.querySelectorAll<HTMLElement>('.view.active, .deals-page, .pricing-page').forEach(element => { element.scrollTop = 0; }));
  };

  useEffect(() => {
    const handleHashChange = () => {
      const view = window.location.hash.replace('#', '');
      if (isRouterView(view)) {
        setActiveView(view);
        localStorage.setItem('clientix_active_view', view);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    const handleNavigate = (event: Event) => {
      const detail = (event as CustomEvent<NavigateDetail>).detail;
      if (!detail?.view || !isRouterView(detail.view)) return;
      if (detail.entityType === 'conversation' && detail.entityId) {
        setSelectedChatId(detail.entityId);
      }
      changeActiveView(detail.view);
    };
    window.addEventListener(NAVIGATE_EVENT, handleNavigate);
    return () => window.removeEventListener(NAVIGATE_EVENT, handleNavigate);
    // `changeActiveView` is stable for the lifetime of the component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSidebarOpen) {
      document.body.classList.add('sb-open');
    } else {
      document.body.classList.remove('sb-open');
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    const handleApiError = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail;
      setApiIssue(detail?.message || 'Не удалось обновить данные. Повторите попытку.');
    };
    window.addEventListener('clientix:api-error', handleApiError);
    return () => window.removeEventListener('clientix:api-error', handleApiError);
  }, []);

  useEffect(() => {
    if (user) {
      fetchApi('/ai/settings')
        .then(settings => {
          if (settings && settings.autopilot !== undefined) {
            setAutopilot(!!settings.autopilot);
          }
        })
        .catch(console.error);
    }
  }, [user]);

  const handleAutopilotToggle = (v: boolean) => {
    setAutopilot(v);
    fetchApi('/ai/settings', {
      method: 'PATCH',
      body: JSON.stringify({ autopilot: v })
    }).catch(console.error);
  };

  const handleNavigateToChat = (chatId: number) => {
    setSelectedChatId(chatId);
    changeActiveView('chats');
  };

  if (loadingAuth) {
    return (
      <div className="app-loading" role="status" aria-live="polite">
        <div className="app-loading-mark" aria-hidden="true" />
        <div className="app-loading-line wide" aria-hidden="true" />
        <div className="app-loading-line" aria-hidden="true" />
        <span>Загрузка рабочего пространства…</span>
      </div>
    );
  }

  if (!user) {
    // Registration requires accepting the offer and the privacy policy, so both
    // have to be readable without an account.
    if (activeView === 'legal') {
      return (
        <>
          <SeoHead activeView={activeView} />
          <LegalPage />
        </>
      );
    }
    return (
      <>
        <SeoHead activeView={activeView} />
        <AuthModal />
      </>
    );
  }

  return (
    <div className={`app ${isSidebarOpen ? 'sb-open' : ''}`}>
      <SeoHead activeView={activeView} />
      <Sidebar
        activeView={activeView}
        setActiveView={changeActiveView}
        autopilot={autopilot}
        setAutopilot={handleAutopilotToggle}
      />

      <div
        className="overlay"
        onClick={() => setIsSidebarOpen(false)}
      ></div>

      <div className="main">
        <Topbar
          onMenuClick={() => setIsSidebarOpen(true)}
          user={user}
          onNavigateToBilling={() => changeActiveView('billing')}
          onNavigateToProfile={() => changeActiveView('profile')}
          onNavigateToSettings={() => changeActiveView('settings')}
        />

        <EmailVerificationBanner />

        {apiIssue && (
          <div className="api-issue-banner" role="alert">
            <WifiOff size={18} aria-hidden="true" />
            <div>
              <strong>Данные временно не обновляются</strong>
              <span>{apiIssue}</span>
            </div>
            <button type="button" onClick={() => window.location.reload()}>
              <RotateCcw size={16} aria-hidden="true" />
              Повторить
            </button>
          </div>
        )}

        <div className="content">
          {activeView === 'dashboard' && <Dashboard setActiveView={changeActiveView} />}
          {activeView === 'chats' && (
            <Chats
              autopilot={autopilot}
              setAutopilot={handleAutopilotToggle}
              selectedChatId={selectedChatId}
            />
          )}
          {activeView === 'requests' && (
            <DealsPage
              setActiveView={changeActiveView}
              onNavigateToChat={handleNavigateToChat}
            />
          )}
          {activeView === 'warehouse' && <Warehouse />}
          {activeView === 'ai' && <AiAgent autopilot={autopilot} setAutopilot={setAutopilot} />}
          {activeView === 'integrations' && <Integrations />}
          {activeView === 'team' && <TeamPage />}
          {activeView === 'billing' && <PricingPage user={user} />}
          {activeView === 'profile' && <ProfilePage />}
          {activeView === 'settings' && <SettingsPage />}
          {activeView === 'approvals' && <ApprovalsPage />}
          {activeView === 'legal' && <LegalPage />}
        </div>
      </div>
    </div>
  );
}

export default App;
